import {
  X_ALLOWED_MEDIA_TYPES,
  X_MAX_IMAGE_BYTES,
  X_MAX_MEDIA_PER_POST,
  type MediaAttachment,
} from '@postpilot/core/domain';
import { AnimatePresence, motion } from 'framer-motion';
import { ImagePlus, Loader2, X } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Image attachments.
 *
 * Uploads immediately on selection rather than on submit. The upload is the slow
 * part; doing it at submit time would make "Post now" hang for several seconds
 * with no explanation, and a failure would happen *after* the user thought they
 * were done.
 */
export function MediaUploader({
  media,
  onChange,
  disabled,
}: {
  media: MediaAttachment[];
  onChange: (media: MediaAttachment[]) => void;
  disabled?: boolean;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);

  const remaining = X_MAX_MEDIA_PER_POST - media.length - uploading;
  const canAdd = remaining > 0 && !disabled;

  const upload = async (files: FileList | File[]) => {
    const list = Array.from(files).slice(0, remaining);
    if (list.length === 0) return;

    // Validate before uploading — a 5 MB round trip that ends in "too big" is a
    // waste of the user's time and our bandwidth.
    const valid: File[] = [];
    for (const file of list) {
      if (!X_ALLOWED_MEDIA_TYPES.includes(file.type as (typeof X_ALLOWED_MEDIA_TYPES)[number])) {
        toast.error(`${file.name} isn’t a supported image`, {
          description: 'X accepts JPEG, PNG, WebP and GIF.',
        });
        continue;
      }
      if (file.size > X_MAX_IMAGE_BYTES) {
        toast.error(`${file.name} is too large`, {
          description: `X caps images at ${Math.floor(X_MAX_IMAGE_BYTES / 1024 / 1024)} MB.`,
        });
        continue;
      }
      valid.push(file);
    }

    if (valid.length === 0) return;

    setUploading((n) => n + valid.length);

    // Sequential. Parallel uploads of four 5 MB images saturate a domestic
    // uplink and all four get slower — and one failing mid-flight leaves the
    // count wrong.
    const uploaded: MediaAttachment[] = [];
    for (const file of valid) {
      try {
        uploaded.push(await api.media.upload(file));
      } catch (error) {
        toast.error(`Could not upload ${file.name}`, {
          description: error instanceof ApiError ? error.message : 'Please try again.',
        });
      } finally {
        setUploading((n) => n - 1);
      }
    }

    if (uploaded.length > 0) {
      onChange([...media, ...uploaded]);
    }
  };

  const remove = async (item: MediaAttachment) => {
    // Drop it from the form first — the user should not wait on a delete.
    onChange(media.filter((m) => m.path !== item.path));
    // Then clean up storage. If this fails we have an orphaned file, which is
    // cheap; blocking the UI on it would not be.
    try {
      await api.media.remove(item.path);
    } catch {
      /* Orphaned object; a storage lifecycle rule can sweep it. */
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={X_ALLOWED_MEDIA_TYPES.join(',')}
        multiple
        className="hidden"
        onChange={(event) => {
          if (event.target.files) void upload(event.target.files);
          // Reset, or selecting the same file twice in a row fires no change event.
          event.target.value = '';
        }}
      />

      {media.length > 0 || uploading > 0 ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {media.map((item) => (
              <motion.div
                key={item.path}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.15 }}
                className="bg-muted group relative aspect-square overflow-hidden rounded-lg border"
              >
                <img src={item.url} alt={item.altText ?? ''} className="size-full object-cover" />

                <button
                  type="button"
                  onClick={() => void remove(item)}
                  className="absolute right-1.5 top-1.5 rounded-full bg-black/70 p-1 text-white opacity-0 transition-opacity hover:bg-black/90 focus-visible:opacity-100 group-hover:opacity-100"
                  aria-label="Remove image"
                >
                  <X className="size-3" />
                </button>
              </motion.div>
            ))}

            {Array.from({ length: uploading }).map((_, i) => (
              <motion.div
                key={`uploading-${i}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-muted flex aspect-square items-center justify-center rounded-lg border"
              >
                <Loader2 className="text-muted-foreground size-4 animate-spin" />
              </motion.div>
            ))}
          </AnimatePresence>

          {canAdd && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="hover:border-primary/40 hover:bg-accent flex aspect-square items-center justify-center rounded-lg border border-dashed transition-colors"
              aria-label="Add another image"
            >
              <ImagePlus className="text-muted-foreground size-4" />
            </button>
          )}
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (canAdd) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (canAdd && e.dataTransfer.files.length) void upload(e.dataTransfer.files);
          }}
          className={cn(
            'rounded-lg border border-dashed p-4 text-center transition-colors',
            dragging && 'border-primary bg-accent',
            !canAdd && 'opacity-50',
          )}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!canAdd}
            onClick={() => inputRef.current?.click()}
            className="text-muted-foreground"
          >
            <ImagePlus />
            Add images
          </Button>
          <p className="text-muted-foreground mt-1 text-xs">
            or drop them here · up to {X_MAX_MEDIA_PER_POST},{' '}
            {Math.floor(X_MAX_IMAGE_BYTES / 1024 / 1024)} MB each
          </p>
        </div>
      )}
    </div>
  );
}
